import { useState, type ReactNode } from 'react';
import { ArrowDown2, More } from 'iconsax-react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@design-system/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@design-system/dropdown-menu';
import { cn } from '@design-system/helpers';

export type ContentMenuItem = {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  destructive?: boolean;
  disabled?: boolean;
};

/**
 * One row in a content tab: a title line, an optional expandable body and the
 * date stamp under it, with a ⋯ menu that appears on hover or focus.
 */
export function ContentCard({
  title,
  badge,
  meta,
  menu = [],
  children,
  className,
  ...props
}: {
  title: ReactNode;
  /** Sits beside the title: a status icon or a small label. */
  badge?: ReactNode;
  meta: ReactNode;
  menu?: ContentMenuItem[];
  /** When given, the card expands to show it below the title. */
  children?: ReactNode;
  className?: string;
} & Omit<React.ComponentProps<'div'>, 'title' | 'children'>) {
  const [open, setOpen] = useState(false);
  const expandable = children !== undefined && children !== null;

  const actions = (
    <span className='flex shrink-0 items-center gap-1'>
      {menu.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type='button'
              aria-label='More actions'
              className='rounded-md p-1 text-gray-500 opacity-0 transition-opacity hover:bg-primary-50 focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100'
            >
              <More
                size={18}
                aria-hidden='true'
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            {menu.map((item) => (
              <DropdownMenuItem
                key={item.label}
                disabled={item.disabled}
                variant={item.destructive ? 'destructive' : 'default'}
                onSelect={item.onSelect}
                className={cn(item.destructive && 'text-red-500')}
              >
                {item.icon}
                {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {expandable && (
        <CollapsibleTrigger asChild>
          <button
            type='button'
            aria-label={open ? 'Collapse' : 'Expand'}
            className='rounded-md p-1 text-gray-600 hover:bg-primary-50'
          >
            <ArrowDown2
              size={18}
              aria-hidden='true'
              className={cn('transition-transform', open && 'rotate-180')}
            />
          </button>
        </CollapsibleTrigger>
      )}
    </span>
  );

  const body = (
    <div
      data-slot='content-card'
      className={cn(
        'group rounded-xl border border-primary-100 bg-white px-4 py-3 transition-colors hover:border-primary-200',
        className
      )}
      {...props}
    >
      <div className='flex items-start justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-2 font-medium text-gray-800'>
          <span className='truncate'>{title}</span>
          {badge}
        </div>
        {actions}
      </div>
      {expandable && (
        <CollapsibleContent className='mt-1 text-sm text-gray-700'>
          {children}
        </CollapsibleContent>
      )}
      <p className='mt-1 text-sm text-gray-800'>{meta}</p>
    </div>
  );

  if (!expandable) return body;
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
    >
      {body}
    </Collapsible>
  );
}
